/**
 * Subscription utilities for managing subscription-based features
 */

export interface SubscriptionInfo {
  isSubscribed: boolean;
  subscriptionDate?: Date;
  tier?: string;
  status?: 'active' | 'cancelled' | 'expired';
}

/**
 * Calculate days since subscription started
 */
export const getDaysSinceSubscription = (subscriptionDate: Date): number => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - subscriptionDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Check if user should see donation orb based on subscription timing
 * Shows after: 10, 30, 60, 90, 120, 180, 270, 365 days, then every 365 days
 */
export const shouldShowDonationOrb = (subscriptionInfo: SubscriptionInfo): boolean => {
  // Don't show if user is not logged in or not subscribed
  if (!subscriptionInfo.isSubscribed || !subscriptionInfo.subscriptionDate) {
    return false;
  }

  // Don't show if subscription is not active
  if (subscriptionInfo.status !== 'active') {
    return false;
  }

  const daysSinceSubscription = getDaysSinceSubscription(subscriptionInfo.subscriptionDate);
  
  // Define the intervals where donation orb should appear
  const showIntervals = [10, 30, 60, 90, 120, 180, 270, 365];
  
  // Check if current day matches any of the intervals
  for (const interval of showIntervals) {
    if (daysSinceSubscription === interval) {
      return true;
    }
  }
  
  // After first year, show every 365 days
  if (daysSinceSubscription > 365) {
    const yearsSinceFirst = Math.floor((daysSinceSubscription - 365) / 365);
    const daysSinceLastYear = (daysSinceSubscription - 365) % 365;
    return daysSinceLastYear === 0;
  }
  
  return false;
};

/**
 * Get next donation orb show date
 */
export const getNextDonationOrbDate = (subscriptionInfo: SubscriptionInfo): Date | null => {
  if (!subscriptionInfo.isSubscribed || !subscriptionInfo.subscriptionDate) {
    return null;
  }

  const daysSinceSubscription = getDaysSinceSubscription(subscriptionInfo.subscriptionDate);
  const showIntervals = [10, 30, 60, 90, 120, 180, 270, 365];
  
  // Find next interval
  for (const interval of showIntervals) {
    if (daysSinceSubscription < interval) {
      const nextDate = new Date(subscriptionInfo.subscriptionDate);
      nextDate.setDate(nextDate.getDate() + interval);
      return nextDate;
    }
  }
  
  // After first year, calculate next annual occurrence
  if (daysSinceSubscription >= 365) {
    const nextAnnualDate = new Date(subscriptionInfo.subscriptionDate);
    const yearsToAdd = Math.floor(daysSinceSubscription / 365) + 1;
    nextAnnualDate.setFullYear(nextAnnualDate.getFullYear() + yearsToAdd);
    return nextAnnualDate;
  }
  
  return null;
};

/**
 * Check if today is a donation orb day and show for limited time
 * Orb shows for 24 hours on the designated day
 */
export const isDonationOrbDayActive = (subscriptionInfo: SubscriptionInfo): boolean => {
  if (!shouldShowDonationOrb(subscriptionInfo)) {
    return false;
  }

  // Additional check: Has user already seen/dismissed orb today?
  const today = new Date().toDateString();
  const lastSeen = localStorage.getItem('dana_orb_last_seen');
  const dismissed = localStorage.getItem('dana_orb_dismissed_today');
  
  if (dismissed === today) {
    return false; // User dismissed it today
  }
  
  return true;
};

/**
 * Mark donation orb as seen for today
 */
export const markDonationOrbSeen = (): void => {
  const today = new Date().toDateString();
  localStorage.setItem('dana_orb_last_seen', today);
};

/**
 * Mark donation orb as dismissed for today
 */
export const markDonationOrbDismissed = (): void => {
  const today = new Date().toDateString();
  localStorage.setItem('dana_orb_dismissed_today', today);
};

/**
 * Get subscription info from RevenueCat context or localStorage
 * This is a mock implementation - replace with actual RevenueCat integration
 */
export const getSubscriptionInfo = (): SubscriptionInfo => {
  // Try to get from localStorage (for testing)
  const stored = localStorage.getItem('manivo_subscription_info');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        subscriptionDate: parsed.subscriptionDate ? new Date(parsed.subscriptionDate) : undefined
      };
    } catch (error) {
      console.error('Error parsing subscription info:', error);
    }
  }

  // Default to not subscribed
  return {
    isSubscribed: false
  };
};

/**
 * Set subscription info (for testing purposes)
 */
export const setSubscriptionInfoForTesting = (info: SubscriptionInfo): void => {
  localStorage.setItem('manivo_subscription_info', JSON.stringify(info));
};

/**
 * Get formatted message for donation orb based on days since subscription
 */
export const getDonationOrbMessage = (subscriptionInfo: SubscriptionInfo): string => {
  if (!subscriptionInfo.subscriptionDate) {
    return "Practice Dana. Support your manifestation journey";
  }

  const days = getDaysSinceSubscription(subscriptionInfo.subscriptionDate);
  
  if (days === 10) {
    return "🌱 10 days of manifestation! Consider blessing others on your journey";
  } else if (days === 30) {
    return "✨ 30 days of transformation! Share your abundance with dana";
  } else if (days === 60) {
    return "🚀 2 months of growth! Practice dana to amplify your manifestations";
  } else if (days === 90) {
    return "🎉 3 months of manifestation mastery! Give back with gratitude";
  } else if (days === 120) {
    return "💫 4 months of positive change! Let dana be part of your practice";
  } else if (days === 180) {
    return "🌟 6 months of transformation! Celebrate with generous giving";
  } else if (days === 270) {
    return "🏆 9 months of manifestation excellence! Practice dana today";
  } else if (days === 365) {
    return "🎊 One full year of manifestation! Honor your journey with dana";
  } else if (days > 365) {
    const years = Math.floor(days / 365);
    return `🌈 ${years} year${years > 1 ? 's' : ''} of manifestation mastery! Practice dana to celebrate`;
  }
  
  return "Practice Dana - Give with gratitude and abundance";
}; 